import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { collection, getDocs, doc, updateDoc, addDoc, deleteDoc, query, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase';
import { 
  FiGrid, FiEdit3, FiSave, FiX, FiPlus, FiTrash2, FiGlobe, 
  FiMapPin, FiMail, FiPhone, FiUser, FiCalendar, FiClock, FiDollarSign, FiUsers
} from 'react-icons/fi';
import LoadingSpinner from '../../../components/common/LoadingSpinner';
import ConfirmationModal from '../../../components/common/ConfirmationModal';
import { blockApi } from '../../../services/api';
import './FranchiseeManagement.css';

const FranchiseeManagement = () => {
  const { userProfile } = useAuth();
  const [franchisees, setFranchisees] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingFranchisee, setEditingFranchisee] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  // Use localStorage to persist franchise selection across tab navigation
  const [selectedFranchisee, setSelectedFranchisee] = useState(() => {
    const saved = localStorage.getItem('selectedFranchiseeId');
    return saved || null;
  });

  // Confirmation Modal State
  const [confirmationModal, setConfirmationModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    actionType: 'delete',
    itemName: '',
    onConfirm: null
  });

  // Form state for editing/adding
  const [formData, setFormData] = useState({
    name: '',
    ownerName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    timezone: 'America/Los_Angeles',
    status: 'active',
    monthlyRevenue: 0,
    cumulativeRevenue: 0,
    establishedDate: '',
    notes: ''
  });

  // Available timezones
  const timezones = [
    { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
    { value: 'America/Denver', label: 'Mountain Time (MT)' },
    { value: 'America/Chicago', label: 'Central Time (CT)' },
    { value: 'America/New_York', label: 'Eastern Time (ET)' },
    { value: 'America/Phoenix', label: 'Arizona Time (MST)' },
    { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
    { value: 'Pacific/Honolulu', label: 'Hawaii Time (HST)' }
  ];



  // Confirmation Modal Helper Functions
  const openConfirmationModal = (config) => {
    setConfirmationModal({
      isOpen: true,
      ...config
    });
  };

  const closeConfirmationModal = () => {
    setConfirmationModal(prev => ({
      ...prev,
      isOpen: false,
      onConfirm: null
    }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch franchisees
      const franchiseesQuery = query(collection(db, 'franchises'), orderBy('name'));
      const franchiseesSnapshot = await getDocs(franchiseesQuery);
      const franchiseesData = franchiseesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setFranchisees(franchiseesData);
    
    // Set selected franchise - prioritize saved selection from localStorage
    if (franchiseesData.length > 0) {
      const savedFranchiseeId = localStorage.getItem('selectedFranchiseeId');
      const savedFranchisee = savedFranchiseeId ? 
        franchiseesData.find(f => f.id === savedFranchiseeId) : null;
      
      if (savedFranchisee) {
        // Use saved selection if it exists and is valid
        setSelectedFranchisee(savedFranchisee);
      } else if (!selectedFranchisee) {
        // Fall back to first franchise if no saved selection
        const defaultFranchisee = franchiseesData[0];
        setSelectedFranchisee(defaultFranchisee);
        localStorage.setItem('selectedFranchiseeId', defaultFranchisee.id);
      }
    }  

      // Fetch blocks data
      try {
        const data = await blockApi.getBlocksWithStats();
        setBlocks(data.blocks || []);
      } catch (err) {
        console.warn('Failed to fetch blocks data:', err.message);
        setBlocks([]);
      }
    } catch (err) {
      setError('Failed to load data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (franchisee) => {
    setEditingFranchisee(franchisee.id);
    setFormData({
      name: franchisee.name || '',
      ownerName: franchisee.ownerName || '',
      email: franchisee.email || '',
      phone: franchisee.phone || '',
      address: franchisee.address || '',
      city: franchisee.city || '',
      state: franchisee.state || '',
      zipCode: franchisee.zipCode || '',
      timezone: franchisee.timezone || 'America/Los_Angeles',
      status: franchisee.status || 'active',
      monthlyRevenue: franchisee.monthlyRevenue || 0,
      cumulativeRevenue: franchisee.cumulativeRevenue || 0,
      establishedDate: franchisee.establishedDate || '',
      notes: franchisee.notes || ''
    });
  };

  const handleSave = async (franchiseeId) => {
    try {
      setError('');
      const franchiseeRef = doc(db, 'franchises', franchiseeId);
      await updateDoc(franchiseeRef, {
        ...formData,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.uid
      });
      
      // Update local state
      setFranchisees(prev => prev.map(f => 
        f.id === franchiseeId ? { ...f, ...formData } : f
      ));
      
      setEditingFranchisee(null);
      setSuccess('Franchisee updated successfully!');
    } catch (err) {
      setError('Failed to save franchisee: ' + err.message);
    }
  };

  const handleAdd = async () => {
    try {
      setError('');
      const newFranchisee = {
        ...formData,
        createdAt: new Date().toISOString(),
        createdBy: userProfile?.uid,
        updatedAt: new Date().toISOString(),
        updatedBy: userProfile?.uid
      };
      
      const docRef = await addDoc(collection(db, 'franchises'), newFranchisee);
      
      // Update local state
      setFranchisees(prev => [...prev, { id: docRef.id, ...newFranchisee }]);
      
      setShowAddForm(false);
      resetForm();
      setSuccess('Franchisee added successfully!');
    } catch (err) {
      setError('Failed to add franchisee: ' + err.message);
    }
  };

  const handleDelete = (franchiseeId) => {
    const franchisee = franchisees.find(f => f.id === franchiseeId);
    const franchiseeName = franchisee ? franchisee.name : franchiseeId;
    
    openConfirmationModal({
      title: 'Delete Franchisee',
      message: 'This will permanently delete this franchisee and all associated data, including location settings, timezone configurations, and historical records.',
      actionType: 'delete',
      itemName: franchiseeName,
      onConfirm: () => confirmDelete(franchiseeId)
    });
  };

  const confirmDelete = async (franchiseeId) => {
    try {
      setError('');
      await deleteDoc(doc(db, 'franchises', franchiseeId));
      
      // Update local state
      setFranchisees(prev => prev.filter(f => f.id !== franchiseeId));
      
      setSuccess('Franchisee deleted successfully!');
    } catch (err) {
      setError('Failed to delete franchisee: ' + err.message);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      ownerName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zipCode: '',
      timezone: 'America/Los_Angeles',
      status: 'active',
      monthlyRevenue: 0,
      cumulativeRevenue: 0,
      establishedDate: '',
      notes: ''
    });
  };

  const handleCancel = () => {
    setEditingFranchisee(null);
    setShowAddForm(false);
    resetForm();
    setError('');
  };

  const getCurrentTime = (timezone) => {
    try {
      return new Date().toLocaleString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZoneName: 'short'
      });
    } catch (err) {
      return 'Invalid timezone';
    }
  };

  // Calculate business statistics
  const businessStats = {
    totalFranchisees: franchisees.length,
    activeFranchisees: franchisees.filter(f => f.status === 'active').length,
    totalBlocks: blocks.length,
    totalMonthlyRevenue: franchisees.reduce((sum, f) => sum + (f.monthlyRevenue || 0), 0),
    totalCumulativeRevenue: franchisees.reduce((sum, f) => sum + (f.cumulativeRevenue || 0), 0)
  };

  // Helper function to get block count for each franchisee
  const getBlockCount = (franchiseeId) => {
    return blocks.filter(block => block.assignedFranchise === franchiseeId).length;
  };

  // Helper function to get franchisee revenue (cumulative from blocks)
  const getFranchiseeRevenue = (franchiseeId) => {
    return blocks.filter(block => block.assignedFranchise === franchiseeId)
                 .reduce((total, block) => total + (block.cumulativeRevenue || 0), 0);
  };

  // Handle franchise selection
  const handleFranchiseSelect = (franchisee) => {
    setSelectedFranchisee(franchisee);
    // Persist selection to localStorage
    if (franchisee) {
      localStorage.setItem('selectedFranchiseeId', franchisee.id);
    } else {
      localStorage.removeItem('selectedFranchiseeId');
    }
  };

  // Calculate totals for selected franchise or all franchises
  const calculateTotals = () => {
    if (selectedFranchisee) {
      // Show data for selected franchise only
      const franchiseBlocks = blocks.filter(block => block.assignedFranchise === selectedFranchisee.id);
      return {
        totalMonthlyRevenue: selectedFranchisee.monthlyRevenue || 0,
        totalCumulativeRevenue: getFranchiseeRevenue(selectedFranchisee.id),
        totalBlocks: franchiseBlocks.length,
        totalFranchisees: 1,
        activeFranchisees: selectedFranchisee.status === 'active' ? 1 : 0
      };
    } else {
      // Show data for all franchises
      return {
        totalMonthlyRevenue: franchisees.reduce((total, f) => total + (f.monthlyRevenue || 0), 0),
        totalCumulativeRevenue: franchisees.reduce((total, f) => total + getFranchiseeRevenue(f.id), 0),
        totalBlocks: blocks.length,
        totalFranchisees: franchisees.length,
        activeFranchisees: franchisees.filter(f => f.status === 'active').length
      };
    }
  };

  const totals = calculateTotals();

  if (loading) {
    return <LoadingSpinner />;
  }

  // Check if user has admin permissions
  if (!userProfile || userProfile.globalRole !== 'administrator') {
    return (
      <div className="franchisee-management">
        <div className="error-state">
          <FiGrid className="error-icon" />
          <h2>Access Restricted</h2>
          <p>You need administrator permissions to manage franchisees.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="franchisee-management">
      <div className="page-header">
        <div className="header-content">
          <FiGrid className="header-icon" />
          <div>
            <h1>Franchisee Management</h1>
            <p>Manage all franchisees, locations, and business performance</p>
          </div>
        </div>
        <button className="btn-primary" onClick={() => setShowAddForm(true)}>
          <FiPlus className="btn-icon" />
          Add Franchisee
        </button>
      </div>

      {error && (
        <div className="alert alert-error">
          <FiX className="alert-icon" />
          {error}
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <FiSave className="alert-icon" />
          {success}
        </div>
      )}

      {/* Business Overview Stats - Prominently at the top */}
      <div className="business-overview">
        <h2>Business Overview{selectedFranchisee ? ` - ${selectedFranchisee.name}` : ''}</h2>
        <div className="business-stats">
          <div className="stat-card primary">
            <FiDollarSign className="stat-icon" />
            <div className="stat-value">${totals.totalMonthlyRevenue.toLocaleString()}</div>
            <div className="stat-label">Total Monthly Revenue</div>
          </div>
          <div className="stat-card secondary">
            <FiDollarSign className="stat-icon" />
            <div className="stat-value">${totals.totalCumulativeRevenue.toLocaleString()}</div>
            <div className="stat-label">Total Cumulative Revenue</div>
          </div>
          <div className="stat-card info">
            <FiGrid className="stat-icon" />
            <div className="stat-value">{totals.totalBlocks}</div>
            <div className="stat-label">Total Blocks</div>
          </div>
          <div className="stat-card success">
            <FiUsers className="stat-icon" />
            <div className="stat-value">{totals.totalFranchisees}</div>
            <div className="stat-label">Total Franchisees</div>
          </div>
          <div className="stat-card warning">
            <FiUsers className="stat-icon" />
            <div className="stat-value">{totals.activeFranchisees}</div>
            <div className="stat-label">Active Franchisees</div>
          </div>
        </div>
      </div>

      {/* Add Franchisee Form */}
      {showAddForm && (
        <div className="franchisee-form-card">
          <div className="form-header">
            <h3>Add New Franchisee</h3>
            <button className="btn-icon-only" onClick={handleCancel}>
              <FiX />
            </button>
          </div>
          <FranchiseeForm 
            formData={formData}
            setFormData={setFormData}
            timezones={timezones}
            onSave={handleAdd}
            onCancel={handleCancel}
            isNew={true}
          />
        </div>
      )}

      {/* Franchisees List */}
      <div className="franchisees-section">
        <h2>Franchisee Details</h2>
        <div className="franchisees-grid">
          {franchisees.map(franchisee => (
            <div 
              key={franchisee.id} 
              className={`franchisee-card ${selectedFranchisee?.id === franchisee.id ? 'selected' : ''}`}
              onClick={() => handleFranchiseSelect(franchisee)}
            >
              {editingFranchisee === franchisee.id ? (
                <div className="franchisee-form">
                  <div className="form-header">
                    <h3>Edit Franchisee</h3>
                    <button className="btn-icon-only" onClick={handleCancel}>
                      <FiX />
                    </button>
                  </div>
                  <FranchiseeForm 
                    formData={formData}
                    setFormData={setFormData}
                    timezones={timezones}
                    onSave={() => handleSave(franchisee.id)}
                    onCancel={handleCancel}
                    isNew={false}
                  />
                </div>
              ) : (
                <FranchiseeCard 
                  franchisee={franchisee}
                  getCurrentTime={getCurrentTime}
                  blockCount={getBlockCount(franchisee.id)}
                  actualRevenue={getFranchiseeRevenue(franchisee.id)}
                  isSelected={selectedFranchisee?.id === franchisee.id}
                  onEdit={(e) => {
                    e.stopPropagation();
                    handleEdit(franchisee);
                  }}
                  onDelete={(e) => {
                    e.stopPropagation();
                    handleDelete(franchisee.id);
                  }}
                  onSelect={() => handleFranchiseSelect(franchisee)}
                />
              )}
            </div>
          ))}
        </div>

        {franchisees.length === 0 && (
          <div className="empty-state">
            <FiGrid className="empty-icon" />
            <h3>No Franchisees Yet</h3>
            <p>Start by adding your first franchisee to the system.</p>
            <button className="btn-primary" onClick={() => setShowAddForm(true)}>
              <FiPlus className="btn-icon" />
              Add First Franchisee
            </button>
          </div>
        )}
      </div>

      {/* Beautiful Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmationModal.isOpen}
        onClose={closeConfirmationModal}
        onConfirm={confirmationModal.onConfirm}
        title={confirmationModal.title}
        message={confirmationModal.message}
        actionType={confirmationModal.actionType}
        itemName={confirmationModal.itemName}
      />
    </div>
  );
};

// Reusable Franchisee Form Component
const FranchiseeForm = ({ formData, setFormData, timezones, onSave, onCancel, isNew }) => {
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <div className="franchisee-form-content">
      <div className="form-grid">
        <div className="form-group">
          <label>
            <FiGrid className="form-icon" />
            Franchise Name *
          </label>
          <input
            type="text"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="Downtown Sunscreen Station"
            required
          />
        </div>

        <div className="form-group">
          <label>
            <FiUser className="form-icon" />
            Owner Name *
          </label>
          <input
            type="text"
            value={formData.ownerName}
            onChange={(e) => handleInputChange('ownerName', e.target.value)}
            placeholder="John Smith"
            required
          />
        </div>

        <div className="form-group">
          <label>
            <FiMail className="form-icon" />
            Email
          </label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            placeholder="owner@franchise.com"
          />
        </div>

        <div className="form-group">
          <label>
            <FiPhone className="form-icon" />
            Phone
          </label>
          <input
            type="tel"
            value={formData.phone}
            onChange={(e) => handleInputChange('phone', e.target.value)}
            placeholder="(555) 123-4567"
          />
        </div>

        <div className="form-group full-width">
          <label>
            <FiMapPin className="form-icon" />
            Address
          </label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            placeholder="123 Main Street"
          />
        </div>

        <div className="form-group">
          <label>City</label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            placeholder="Los Angeles"
          />
        </div>

        <div className="form-group">
          <label>State</label>
          <input
            type="text"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            placeholder="CA"
          />
        </div>

        <div className="form-group">
          <label>Zip Code</label>
          <input
            type="text"
            value={formData.zipCode}
            onChange={(e) => handleInputChange('zipCode', e.target.value)}
            placeholder="90210"
          />
        </div>

        <div className="form-group">
          <label>
            <FiGlobe className="form-icon" />
            Timezone *
          </label>
          <select
            value={formData.timezone}
            onChange={(e) => handleInputChange('timezone', e.target.value)}
            required
          >
            {timezones.map(tz => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Status</label>
          <select
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
          >
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="pending">Pending</option>
            <option value="suspended">Suspended</option>
          </select>
        </div>

        <div className="form-group">
          <label>Monthly Revenue Target</label>
          <input
            type="number"
            value={formData.monthlyRevenue}
            onChange={(e) => handleInputChange('monthlyRevenue', parseFloat(e.target.value) || 0)}
            placeholder="5000"
            min="0"
            step="100"
          />
        </div>

        <div className="form-group">
          <label>Cumulative Revenue</label>
          <input
            type="number"
            value={formData.cumulativeRevenue}
            onChange={(e) => handleInputChange('cumulativeRevenue', parseFloat(e.target.value) || 0)}
            placeholder="25000"
            min="0"
            step="100"
          />
        </div>

        <div className="form-group">
          <label>
            <FiCalendar className="form-icon" />
            Established Date
          </label>
          <input
            type="date"
            value={formData.establishedDate}
            onChange={(e) => handleInputChange('establishedDate', e.target.value)}
          />
        </div>

        <div className="form-group full-width">
          <label>Notes</label>
          <textarea
            value={formData.notes}
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Additional notes about this franchisee..."
            rows="3"
          />
        </div>
      </div>

      <div className="form-actions">
        <button className="btn-secondary" onClick={onCancel}>
          <FiX className="btn-icon" />
          Cancel
        </button>
        <button className="btn-primary" onClick={onSave}>
          <FiSave className="btn-icon" />
          {isNew ? 'Add Franchisee' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

// Reusable Franchisee Card Component
const FranchiseeCard = ({ franchisee, getCurrentTime, blockCount, actualRevenue, onEdit, onDelete }) => {
  return (
    <div className="franchisee-card-content">
      <div className="card-header">
        <h3>{franchisee.name}</h3>
        <span className={`status-badge ${franchisee.status || 'active'}`}>
          {(franchisee.status || 'active').toUpperCase()}
        </span>
      </div>

      <div className="card-body">
        <div className="revenue-section">
          <div className="revenue-item">
            <FiDollarSign className="revenue-icon" />
            <div className="revenue-details">
              <span className="revenue-label">Actual Cumulative Revenue:</span>
              <span className="revenue-value actual">${actualRevenue.toLocaleString()}</span>
            </div>
          </div>
          <div className="revenue-item">
            <span className="revenue-label">Monthly Target:</span>
            <span className="revenue-value target">${(franchisee.monthlyRevenue || 0).toLocaleString()}</span>
          </div>
        </div>

        <div className="card-info">
          <div className="info-item">
            <FiUser className="info-icon" />
            <span className="info-text">{franchisee.ownerName}</span>
          </div>
          
          {franchisee.email && (
            <div className="info-item">
              <FiMail className="info-icon" />
              <span className="info-text">{franchisee.email}</span>
            </div>
          )}

          {franchisee.phone && (
            <div className="info-item">
              <FiPhone className="info-icon" />
              <span className="info-text">{franchisee.phone}</span>
            </div>
          )}

          <div className="info-item">
            <FiMapPin className="info-icon" />
            <span className="info-text">
              {franchisee.city && franchisee.state 
                ? `${franchisee.city}, ${franchisee.state}`
                : franchisee.address || 'Address not set'
              }
            </span>
          </div>

          <div className="info-item">
            <FiGlobe className="info-icon" />
            <span className="info-text">{franchisee.timezone || 'America/Los_Angeles'}</span>
          </div>

          <div className="info-item">
            <FiClock className="info-icon" />
            <span className="info-text">Current time: {getCurrentTime(franchisee.timezone || 'America/Los_Angeles')}</span>
          </div>

          <div className="info-item">
            <FiGrid className="info-icon" />
            <span className="info-text">{blockCount} Blocks Assigned</span>
          </div>
        </div>
      </div>

      <div className="card-actions">
        <button className="btn-secondary btn-sm" onClick={onEdit}>
          <FiEdit3 className="btn-icon" />
          Edit
        </button>
        <button className="btn-danger btn-sm" onClick={onDelete}>
          <FiTrash2 className="btn-icon" />
          Delete
        </button>
      </div>
    </div>
  );
};

export default FranchiseeManagement; 